import { Pages } from "@modules/BookingCommon/common/constants"
import type {
    OnlineOrOffline,
    GetBookingDetailsResponse,
    DepositConfiguration,
    SessionTypePartial,
    TimeRequestSelectors,
    DatePickerError,
} from "@modules/BookingCommon/common/interfaces"
import type { PossibleHoursRecord, UtcTime } from "@modules/Booking/common/interfaces"
import type { BookingStatus, OfferedService, Psychologist } from "@utils/interfaces"
import type { ServicesGroup } from "@utils/helpers/ServicesGroup"
import { CURRENCY } from "@utils/constants"
import { SESSION_TYPE } from "@modules/BookingCommon/common/constants/sessionType"
import { PaymentMethod } from "@modules/ClientAccount/utils"

export type BookingFlowMode = "booking" | "payment"

export interface BookingFlowCommonState {
    mode: BookingFlowMode | null

    pages: Pages[]
    currentPage: Pages

    currency: CURRENCY
    onlineOrOffline?: OnlineOrOffline
    isOnline: boolean

    serviceGroups?: ServicesGroup[]
    serviceGroup?: ServicesGroup
    selectedService?: OfferedService
    selectedDiscount?: number

    isLoading: boolean
    isPriceLoading: boolean
    errorMessage?: string

    bookingStatus?: BookingStatus

    // Common for both booking and payment flows
    sessionType: SessionTypePartial
    deposits?: DepositConfiguration[]
    selectedDeposit?: DepositConfiguration
}

export interface BookingFlowBookingState {
    psy?: Psychologist

    // Slot select
    hasSlots: boolean
    slotId?: string
    slotDuration?: number
    selectedTime?: Date
    selectedDayOfMonth?: Date
    currentWeek?: Date

    // User info
    email?: string

    // Session history
    hasPreviousSessions: boolean
    hasPreviousSessionsWithSamePsychologist: boolean

    // UI state
    timeRequestModalActive: boolean
    isChooseManuallyService: boolean
    chooseFlowWithNoAvailable: boolean
    canChangeHistory: boolean
    requestTimesSelectors: TimeRequestSelectors[]
    datePickerIsLoaded: boolean
    datePickerError?: DatePickerError
    possibleHours: PossibleHoursRecord | null
    timeRequestsUtcTime: UtcTime | null
    hash?: string

    // Navigation state
    trFlowFromPsyProfile: boolean
    returnFromBooking: boolean
    wasStateRestored: boolean
}

export interface BookingFlowPaymentState {
    bookingDetails?: GetBookingDetailsResponse
    bookSessionIsLoading: boolean
    payByCard: PaymentMethod | null
}

export interface BookingFlowState extends BookingFlowCommonState {
    booking: BookingFlowBookingState
    payment: BookingFlowPaymentState
}

export const bookingFlowInitialState: BookingFlowState = {
    mode: null,

    pages: [Pages.FORM],
    currentPage: Pages.FORM,

    currency: CURRENCY.RUB,
    onlineOrOffline: undefined,
    isOnline: false,

    serviceGroups: undefined,
    serviceGroup: undefined,
    selectedService: undefined,
    selectedDiscount: undefined,

    isLoading: false,
    isPriceLoading: true,
    errorMessage: undefined,

    bookingStatus: undefined,

    // Common for both flows
    sessionType: SESSION_TYPE.DEFAULT,
    deposits: undefined,
    selectedDeposit: undefined,

    booking: {
        psy: undefined,
        hasSlots: false,
        slotId: undefined,
        slotDuration: undefined,
        selectedTime: undefined,
        selectedDayOfMonth: undefined,
        currentWeek: undefined,
        email: undefined,
        hasPreviousSessions: false,
        hasPreviousSessionsWithSamePsychologist: false,
        timeRequestModalActive: false,
        isChooseManuallyService: false,
        chooseFlowWithNoAvailable: false,
        canChangeHistory: true,
        requestTimesSelectors: [],
        datePickerIsLoaded: false,
        datePickerError: undefined,
        possibleHours: null,
        timeRequestsUtcTime: null,
        hash: undefined,
        trFlowFromPsyProfile: false,
        returnFromBooking: false,
        wasStateRestored: false,
    },
    payment: {
        bookSessionIsLoading: false,
        bookingDetails: undefined,
        payByCard: null,
    },
}
